"use client";
import { Api } from "@/Apis";
import {
  LandingCommentGetListInputApiModel,
  LandingCommentGetListItemOutputApiModel,
} from "@/Apis/ClinicApi";
import CommonCommentChangeStatus from "@/Areas/Common/Pages/CommonComment/ChangeStatus/CommonCommentChangeStatus";
import { Format, Html, If } from "@/Components";
import { Helper, Roles } from "@/Helpers";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { ChildrenOf } from "@/Helpers/CenterOptions.types";
import { useCheckRole, useImageFallback, useT } from "@/Hooks";
import { Avatar, Button, Card, List, Rate, Space, Spin } from "antd";
import _ from "lodash";
import { FC, useEffect, useState } from "react";
import { HiReply } from "react-icons/hi";
import LandingCommentCreateOrEditPage from "../CreateOrEdit/LandingCommentCreateOrEditPage";

const maximumCommentDepth = 3;

export interface LandingCommentListPageProps extends LandingCommentGetListInputApiModel {
  onGetList?: (list: LandingCommentGetListItemOutputApiModel[]) => void;
}

const LandingCommentListPage: FC<LandingCommentListPageProps> = ({
  onGetList,
  ...props
}) => {
  const t = useT();
  const getImageFallback = useImageFallback();
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState<LandingCommentGetListItemOutputApiModel[]>();
  const isAdmin = useCheckRole(Roles.Admin);
  useEffect(() => {
    getList();
  }, []);
  const getList = async () => {
    setLoading(true);
    const { Result } = await Api.LandingComment.getList(props);
    setList(Result);
    onGetList?.(Result ?? []);
    setLoading(false);
  };
  const getComments = (
    list?: LandingCommentGetListItemOutputApiModel[],
    depth = 1,
  ) => {
    return (
      <List
        itemLayout="vertical"
        size="large"
        pagination={false}
        dataSource={list}
        renderItem={(item) => {
          const isParent = depth === 1;
          return (
            <List.Item key={item.Id}>
              <Space orientation="vertical">
                <List.Item.Meta
                  avatar={
                    item.CenterUserCreator && (
                      <Avatar
                        src={getImageFallback({
                          imageUrl: item.CenterUserCreator.ImageUrl,
                          entity: "CenterUser",
                          genderKey: item.CenterUserCreator.GenderKey,
                        })}
                      />
                    )
                  }
                  title={
                    <Space separator="|">
                      {[
                        ...Helper.addItem(
                          `${item.CenterUserCreator?.FirstName} *****`,
                          props.TypeKey !=
                          CenterOptions.Enum.CommentType.Blog.key &&
                          !!item.CenterUserCreator,
                        ),
                        ...Helper.addItem(
                          t(
                            "landing.comment.list.anonymousUser",
                            undefined,
                            "Comments",
                          ),
                          props.TypeKey ==
                          CenterOptions.Enum.CommentType.Blog.key,
                        ),
                        ...Helper.addItem(
                          <CommonCommentChangeStatus
                            onChange={getList}
                            model={item}
                          />,
                          isAdmin,
                        ),
                      ]}
                    </Space>
                  }
                  description={
                    <Space separator="|">
                      {[
                        <Format
                          key="DateTime"
                          type="DateTime"
                          value={item.DateRegister}
                        />,
                        ...Helper.addItem(
                          <Rate
                            key="Role"
                            disabled
                            defaultValue={item.Score}
                          />,
                          isParent,
                        ),
                      ]}
                    </Space>
                  }
                />
                <Html value={item.Text} />

                <If
                  condition={
                    depth < maximumCommentDepth &&
                    item.StatusKey == CenterOptions.Enum.Status.Active.key
                  }
                >
                  <CommentReply item={item} {...props} reload={getList} />
                </If>

                <If
                  condition={
                    depth < maximumCommentDepth && !_.isEmpty(item.Children)
                  }
                >
                  <Card
                    title={t(
                      "landing.comment.list.replies",
                      undefined,
                      "Comments",
                    )}
                  >
                    {getComments(item.Children ?? [], depth + 1)}
                  </Card>
                </If>
              </Space>
            </List.Item>
          );
        }}
      />
    );
  };
  return (
    <Spin spinning={loading}>
      <Space orientation="vertical">
        <Card
          title={
            _.isEmpty(list)
              ? t("landing.profile.tab.comments", undefined, "Comments")
              : t(
                "landing.comment.list.commentCount",
                { value0: list?.length },
                "Comments",
              )
          }
        >
          {getComments(list?.filter((p) => !p.ParentId))}
        </Card>
        <LandingCommentCreateOrEditPage
          values={{ ...props }}
          callback={(result) => {
            if (result) getList();
          }}
        />
      </Space>
    </Spin>
  );
};

export default LandingCommentListPage;

export interface CommentReplyProps {
  TypeKey: ChildrenOf<"CommentType">;
  CenterUserDoctorId?: number;
  BlogId?: number;
  CenterId?: number;
  CenterCommodityId?: number;
  CenterItemId?: number;

  item: LandingCommentGetListItemOutputApiModel;
  reload: () => void;
}

const CommentReply: FC<CommentReplyProps> = ({ item, reload, ...props }) => {
  const t = useT();
  const [reply, setReply] = useState(false);
  return reply ? (
    <LandingCommentCreateOrEditPage
      showCancelButton
      title={t("landing.comment.list.submitReply", undefined, "Comments")}
      values={{ ...props, ParentId: item.Id }}
      callback={(result) => {
        if (result) reload();
        setReply(false);
      }}
    />
  ) : (
    <Button icon={<HiReply />} onClick={() => setReply(true)}>
      {t("common.action.reply", undefined, "Comments")}
    </Button>
  );
};
